import { useState, useRef, useCallback, useEffect } from 'react'
import { Menu } from 'lucide-react'
import { useLocalizedNavigate } from '@/hooks/useLocalizedPath'
import { useTranslation } from 'react-i18next'
import { NavHeaderLinkItem } from '@/gql/schema'
import { MEGA_MENU_CLASSES, MEGA_MENU_CLASSES_HORIZONTAL } from '@/utils/navigation'
import DesktopMegaMenu from './DesktopMegaMenu'
import MobileNav from './MobileNav'

type NavItem = Pick<NavHeaderLinkItem, 'link' | 'children' | 'label'>

export interface NavProps {
  menuItems: NavItem[]
  horizontalNavItems: NavItem[]
  isNavOpen: boolean
  onNavClick: (url: string, isExternal: boolean) => void
  setIsNavOpen: (open: boolean) => void
  className?: string
}

export default function Nav(props: NavProps) {
  const {
    menuItems,
    horizontalNavItems,
    isNavOpen,
    setIsNavOpen,
    className,
  } = props
  const { t } = useTranslation()
  const [isMenuDropdownOpen, setIsMenuDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useLocalizedNavigate()

  const handleMenuNavigate = useCallback(
    (url: string, isExternal: boolean) => {
      if (isExternal) {
        window.open(url, '_blank')
      } else {
        setIsMenuDropdownOpen(false)
        setIsNavOpen(false)
        navigate(url)
      }
    },
    [navigate, setIsNavOpen]
  )

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsMenuDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <>
      {/* Desktop navigation */}
      <nav className={`hidden md:block border-t border-secondary/10 pt-3 pb-1 ${className || ''}`}>
        <div className="flex flex-wrap items-center gap-6 text-sm text-foreground">
          <div className="relative shrink-0" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setIsMenuDropdownOpen(!isMenuDropdownOpen)}
              aria-expanded={isMenuDropdownOpen}
              aria-haspopup="menu"
              className={`flex items-center gap-2 transition-colors cursor-pointer ${isMenuDropdownOpen ? 'text-primary-text' : 'text-primary-text hover:text-secondary'}`}
            >
              <Menu aria-hidden="true" className="w-5 h-5" />
              {t('nav.menu')}
            </button>

            {isMenuDropdownOpen && (
              <div className="absolute top-full left-0 mt-3 w-64 bg-background border border-secondary/10 shadow-lg z-50 rounded-b-md">
                <DesktopMegaMenu
                  orientation="vertical"
                  items={menuItems}
                  classes={MEGA_MENU_CLASSES}
                  onNavigate={handleMenuNavigate}
                />
              </div>
            )}
          </div>

          {horizontalNavItems.length > 0 && (
            <span aria-hidden="true" className="h-5 w-px bg-secondary/30 -mx-2" />
          )}

          {horizontalNavItems.length > 0 && (
            <DesktopMegaMenu
              orientation="horizontal"
              items={horizontalNavItems}
              classes={MEGA_MENU_CLASSES_HORIZONTAL}
              onNavigate={handleMenuNavigate}
            />
          )}
        </div>
      </nav>

      {/* Mobile navigation (collapsible) */}
      {isNavOpen && (
        <div className="flex flex-col gap-6 md:hidden">
          <div className="flex flex-col">
            <h2 className="text-sm font-bold text-foreground/80">{t('nav.mainMenu')}</h2>
            <nav className={`md:hidden pt-3 border-secondary/10 ${className || ''}`}>
              <MobileNav items={menuItems} onNavigate={handleMenuNavigate} />
            </nav>
          </div>

          <div className="flex flex-col md:hidden">
            <h2 className="text-sm font-bold text-foreground/80">{t('nav.usefulLinks')}</h2>
            <nav className={`md:hidden pt-3 border-secondary/10 ${className || ''}`}>
              <MobileNav items={horizontalNavItems} onNavigate={handleMenuNavigate} />
            </nav>
          </div>
        </div>
      )}
    </>
  )
}
